// This component should support multiple image size style so possibly some of the styles will not be used.
/* eslint-disable relay/unused-fields */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { Props as ArticleImageProps } from "~/components/article/article-image";
import { ArticleImage } from "~/components/article/article-image";
import type { Props as BaseImageProps } from "~/components/common/base-image";
import { useResponsiveImage } from "~/components/common/base-image/hooks";
import { useImageUtils } from "~/lib/hooks";
import type { ResponsiveVariants } from "~/lib/styles/responsive";
import type { articleLeadingImagesContent$key } from "~/queries/__generated__/articleLeadingImagesContent.graphql";

import { ArticleLeadingImageContainer } from "./styles";

export type Props = {
  className?: string;
  imageProps: Omit<BaseImageProps, "$aspectRatio">;
  reference: articleLeadingImagesContent$key;
  resizeImageWidth?: number;
  responsiveVariants: ResponsiveVariants<
    "size1280x720" | "size1020x680" | "size1200x800" | "size768x768"
  >;
  showTitle?: boolean;
} & Pick<ArticleImageProps, "responsiveDescriptionVariants" | "responsiveImageVariants">;

export const ArticleLeadingImages: FunctionComponent<Props> = ({
  className,
  imageProps,
  reference: reference_,
  resizeImageWidth,
  responsiveDescriptionVariants,
  responsiveImageVariants,
  responsiveVariants,
  showTitle = true,
}) => {
  const content = useFragment(
    graphql`
      fragment articleLeadingImagesContent on Content {
        images {
          title
          url
          isSlideshow
          size1280x720: style(filter: { style: "wide_landscape" }) {
            url
          }
          size1200x800: style(filter: { style: "1200x800" }) {
            url
          }
          size1020x680: style(filter: { style: "1020x680" }) {
            url
          }
          size768x768: style(filter: { style: "768x768" }) {
            url
          }
        }
      }
    `,
    reference_,
  );
  const { getResizedImageUrl } = useImageUtils();

  // TODO: support multiple images (Carousel)
  const mainImage = content.images?.find(image => image?.isSlideshow) ?? first(content.images);

  const variantSpecs = {
    size1020x680: { aspectRatio: "3/2", url: mainImage?.size1020x680?.url, width: 1020 },
    size1200x800: { aspectRatio: "3/2", url: mainImage?.size1200x800?.url, width: 1200 },
    size1280x720: { aspectRatio: "16/9", url: mainImage?.size1280x720?.url, width: 1280 },
    size768x768: { aspectRatio: "1/1", url: mainImage?.size768x768?.url, width: 768 },
  };
  const mapVariant = (viewportSize: "desktopUp" | "tabletUp" | "mobileUp") => ({
    aspectRatio: variantSpecs[responsiveVariants[viewportSize]].aspectRatio,
    url: variantSpecs[responsiveVariants[viewportSize]].url ?? "",
    width: variantSpecs[responsiveVariants[viewportSize]].width,
  });

  const responsiveVariant = {
    desktopUp: mapVariant("desktopUp"),
    mobileUp: mapVariant("mobileUp"),
    tabletUp: mapVariant("tabletUp"),
  };

  const { bindResponsiveAspectRatio } = useResponsiveImage(responsiveVariant);
  if (!mainImage) return null;

  const renderImage = () => {
    let imageUrl = mainImage[responsiveVariants.mobileUp]?.url ?? mainImage.url;

    imageUrl = resizeImageWidth ? getResizedImageUrl(imageUrl, resizeImageWidth) : imageUrl;
    return (
      <ArticleImage
        imageProps={{
          ...imageProps,
          $objectFit: "cover",
          responsiveVariant,
          src: imageUrl,
          ...bindResponsiveAspectRatio,
        }}
        responsiveDescriptionVariants={responsiveDescriptionVariants}
        responsiveImageVariants={responsiveImageVariants}
        title={showTitle ? mainImage.title ?? "" : ""}
      />
    );
  };
  return (
    <ArticleLeadingImageContainer className={className}>
      {renderImage()}
    </ArticleLeadingImageContainer>
  );
};

ArticleLeadingImages.displayName = "ArticleLeadingImages";
